import { z } from 'zod';
import {
  LANDING_PAGE_URL_MAX_LEN,
  LIBRARY_BODY_MAX_BYTES,
  LIBRARY_DISPLAY_NAME_MAX_LEN,
  LIBRARY_METADATA_MAX_BYTES,
  LIBRARY_VALUE_MAX_LEN,
  PARAMETER_LIBRARY_TYPES,
  PROMPT_INJECTION_PATTERN,
} from '../constants.js';

// ─── Sub-schemas ──────────────────────────────────────────────────────────────

const libraryTypeSchema = z.enum(PARAMETER_LIBRARY_TYPES);

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

// value: snake_case / kebab-case machine key (auto-converted by server from displayName if needed)
const libraryValueSchema = z
  .string()
  .min(1)
  .max(LIBRARY_VALUE_MAX_LEN)
  .regex(/^[a-z0-9_-]+$/, 'value must be lowercase alphanumeric, underscores, or hyphens');

// displayName flows into the brief block of every prompt (apps/api/src/openai/prompt-builder.ts)
// as ANGLE/AVATAR/PROBLEM/SOLUTION/AWARENESS/INGREDIENT lines. We reject the source-pack
// delimiter to defend against prompt injection via crafted library entries (PRD §16.3).
const libraryDisplayNameSchema = z
  .string()
  .min(1)
  .max(LANDING_PAGE_URL_MAX_LEN)
  .refine((s) => !PROMPT_INJECTION_PATTERN.test(s), 'prompt_injection_pattern');

// metadata is a JSON object capped at 8 KB serialised (UTF-8 byte count)
// TextEncoder.encode() returns the UTF-8 byte length, works in both Node and browser.
const libraryMetadataSchema = z
  .record(z.unknown())
  .refine(
    (m) => new TextEncoder().encode(JSON.stringify(m)).byteLength <= LIBRARY_METADATA_MAX_BYTES,
    'metadata exceeds 8KB'
  );

// body: optional markdown blob — injected into the prompt as the entry's
// descriptive block when present. Capped at LIBRARY_BODY_MAX_BYTES.
const libraryBodySchema = z.string().max(LIBRARY_BODY_MAX_BYTES, 'body exceeds 64KB');

// ─── Request schemas ──────────────────────────────────────────────────────────

export const CreateLibraryEntryRequestSchema = z
  .object({
    type: libraryTypeSchema,
    scope: z.string().min(1), // 'workspace' or 'product:<productId>'
    value: libraryValueSchema.optional(),
    displayName: libraryDisplayNameSchema,
    body: libraryBodySchema.optional(),
    metadata: libraryMetadataSchema.default({}),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'landing_page_url') {
      if (!data.scope.startsWith('product:')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scope'],
          message: 'landing_page_url_requires_product_scope',
        });
      }
      if (!isHttpUrl(data.displayName)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['displayName'],
          message: 'landing_page_url_invalid_url',
        });
      }
      return;
    }

    if (data.displayName.length > LIBRARY_DISPLAY_NAME_MAX_LEN) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        path: ['displayName'],
        maximum: LIBRARY_DISPLAY_NAME_MAX_LEN,
        type: 'string',
        inclusive: true,
        message: `String must contain at most ${LIBRARY_DISPLAY_NAME_MAX_LEN} character(s)`,
      });
    }
  });
export type CreateLibraryEntryRequest = z.infer<typeof CreateLibraryEntryRequestSchema>;

export const UpdateLibraryEntryRequestSchema = z.object({
  displayName: libraryDisplayNameSchema.optional(),
  body: libraryBodySchema.nullable().optional(),
  metadata: libraryMetadataSchema.optional(),
});
export type UpdateLibraryEntryRequest = z.infer<typeof UpdateLibraryEntryRequestSchema>;

export const ListLibraryEntriesQuerySchema = z.object({
  scope: z.string().optional(),
  status: z.enum(['active', 'archived']).optional(),
  limit: z.coerce.number().int().positive().max(200).default(50),
  offset: z.coerce.number().int().nonnegative().default(0),
});
export type ListLibraryEntriesQuery = z.infer<typeof ListLibraryEntriesQuerySchema>;

// ─── Response schemas ─────────────────────────────────────────────────────────

export const LibraryEntryResponseSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int().positive(),
  type: libraryTypeSchema,
  scope: z.string(),
  value: z.string(),
  displayName: z.string(),
  body: z.string().nullable().optional(),
  metadata: z.record(z.unknown()),
  status: z.enum(['active', 'archived']),
  isCurrent: z.boolean(),
  createdAt: z.string(),
  createdBy: z.string().uuid().nullable().optional(),
});
export type LibraryEntryResponse = z.infer<typeof LibraryEntryResponseSchema>;

export const ListLibraryEntriesResponseSchema = z.object({
  ok: z.literal(true),
  items: z.array(LibraryEntryResponseSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type ListLibraryEntriesResponse = z.infer<typeof ListLibraryEntriesResponseSchema>;
